/*global OffscreenCanvas, document*/

import Color from "./color.js"
import screenImageUtils from "./screen_image_utils.js"

const {getCropParams} = screenImageUtils


const lineDashPresets = {
    dashed:     [6, 4], //eslint-disable-line no-magic-numbers
    dotted:     [2, 3],
    "dash-dot": [8, 4, 2, 4] //eslint-disable-line no-magic-numbers
}


let id = 1
const currentCxStyles = {}



function ScreenCanvasStyleHandler (screen) {

    const {cx} = screen

    if (!cx.id) {
        cx.id = id++
        currentCxStyles[cx.id] = {}
    }

    const currentCxStyle  = currentCxStyles[cx.id]
    screen.currentCxStyle = currentCxStyle
    this.previousGlobalAlpha = cx.globalAlpha

    addMethods(this, {currentCxStyle, cx, screen})

    this.setDefaultStyles()
}




function addMethods (self, {currentCxStyle, cx, screen}) {

    self.setDefaultStyles = function () {
        currentCxStyle.blendMode = "source-over"
        currentCxStyle.filter = "none"
        currentCxStyle.smoothing = true
        cx.lineJoin = "round"
        cx.imageSmoothingEnabled = true
        cx.imageSmoothingQuality = "high" //FIXME benchmark ? add as an option ?
    }


    self.resetStyle = function () {
        for (let key in currentCxStyle) {
            delete currentCxStyle[key]
        }
        self.setDefaultStyles()
    }


    self.resyncWithCurrentStyle = function () {
        cx.strokeStyle = currentCxStyle.stroke
        cx.fillStyle   = currentCxStyle.fill
        cx.lineWidth   = currentCxStyle.lineWidth
        cx.font        = currentCxStyle.font
    }


    self.setStrokeStyle = function (params) {
        const {color, strokeColor = color, lineWidth = 2, strokeTexture} = params
        if (currentCxStyle.lineWidth !== lineWidth) {
            currentCxStyle.lineWidth = lineWidth
            cx.lineWidth             = lineWidth
        }
        if (strokeTexture) {
            const pattern = setTextureStyle(screen, params, "stroke")
            if (pattern) {
                return
            }
        }
        const strokeStyle = strokeColor || "#000"
        if (currentCxStyle.stroke !== strokeStyle) {
            currentCxStyle.stroke = strokeStyle
            cx.strokeStyle        = strokeStyle
        }
    }


    self.setFillStyle = function (params) {
        const {linearGradient, radialGradient, fillColor, color = fillColor, texture} = params
        if (texture) {
            const pattern = setTextureStyle(screen, params, "fill")
            if (pattern) {
                return
            }
            setSimpleFillStyle(screen, {color})
        } else if (linearGradient || radialGradient) {
            setGradientStyle(screen, params)
        } else {
            setSimpleFillStyle(screen, {color})
        }
    }


    const pixelSizeRegex = /[0-9.]+px/
    const familyAfterSizeRegex = /[0-9.]+px(?:\/\S+)?\s+\S/

    // canvas silently ignores cx.font without a font-family (eg "30px" alone)
    function ensureFontFamily (font) {
        if (font.match(pixelSizeRegex) && !font.match(familyAfterSizeRegex)) {
            return `${font} serif`
        }
        return font
    }

    self.setFont = function ({font = "20px serif", width, height}) {

        //FIXME handle cases like 'italic Nasalization'
        if (
            (typeof width !== "undefined" || typeof height !== "undefined") &&
            !font.match(pixelSizeRegex)
        ) {
            font = `20px ${font}`
        }

        font = ensureFontFamily(font)

        if (currentCxStyle.font !== font) {
            currentCxStyle.font = font
            cx.font             = font
        }
    }




    self.stroke = function (params = {}) {
        if (params.lineWidth !== 0) { //because canvas ignores cx.linewidth = 0 instruction
            self.setStrokeStyle(params)
            cx.stroke()
        }
    }


    self.fill = function (params = {}) {
        self.setFillStyle(params)
        cx.fill()
    }



    self.setSpecialStyles = function ({opacity, blendMode, filter = "", shadow, blur, lineDash, lineDashOffset}) { //eslint-disable-line complexity
        if (opacity !== null && opacity !== 1) {
            self.previousGlobalAlpha = cx.globalAlpha
            cx.globalAlpha *= opacity
        }
        if (blendMode) {
            let otherName = operationNames[blendMode]
            if (otherName) {
                blendMode = otherName
            }
            cx.globalCompositeOperation = blendMode
        }

        if (shadow) {
            filter += shadowFilter(shadow)
        }
        if (blur) {
            filter += ` blur(${blur}px)`
        }
        if (filter) {
            cx.filter = filter
        }
        if (lineDash) {
            if (typeof lineDash === "string") {
                lineDash = lineDashPresets[lineDash] || []
            }
            cx.setLineDash(lineDash)
            if (lineDashOffset) {
                cx.lineDashOffset = lineDashOffset
            }
        }
    }


    self.resetSpecialStyles = function () {
        cx.globalAlpha = self.previousGlobalAlpha
        cx.globalCompositeOperation = currentCxStyle.blendMode
        cx.filter = currentCxStyle.filter
        cx.setLineDash([])
        cx.lineDashOffset = 0
    }


    self.setOpacity = function (opacity = 1) {
        self.previousGlobalAlpha = cx.globalAlpha
        cx.globalAlpha = opacity
    }


    self.setSmoothing = function (value) {
        if (currentCxStyle.smoothing !== value) {
            currentCxStyle.smoothing = value
            cx.imageSmoothingEnabled = value
        }
    }


    self.strokeAndFill = function (params = {}) {
        if (hasStrokeStyleParams(params)) {
            self.stroke(params)
        }
        if (hasFillStyleParams(params)) {
            self.fill(params)
        }
    }

}



//see https://drafts.fxtf.org/compositing/#canvascompositingandblending

const operationNames = {
    normal:                "source-over",
    "new || old":          "source-over",
    "old || new":          "destination-over",
    "old && new":          "source-in",
    "new && old":          "destination-in",
    "!old && new":         "source-out",
    "!new && old":         "destination-out",
    "old && (new || old)": "source-atop",
    "new && (old || new)": "destination-atop",
    "old + new":           "lighter",
    "old * new":           "multiply",
    "min(old, new)":       "darken",
    min:                   "darken",
    "max(old, new)":       "lighten",
    max:                   "lighten",
    "abs(old - new)":      "difference",
    "abs(new - old)":      "difference",
    "old ^ new":           "xor",
    "old XOR new":         "xor",
    new:                   "copy"
}


//other are : screen, overlay, color-dodge, color-burn, plus-darker, plus-lighter
//hard-light, soft-light, exclusion, hue, saturation, color, luminosity, clear






function setSimpleFillStyle (screen, {color = "#000"} = {}) {

    if (screen.currentCxStyle.fill !== color) {
        screen.currentCxStyle.fill = color
        screen.cx.fillStyle        = color
    }
}




const defaultShadowOpacity = 0.5
const defaultShadowBlur    = 5

function shadowFilter ({
    xOffset = 0,
    yOffset = 0,
    color = "black",
    blur: shadowBlur = defaultShadowBlur,
    opacity: shadowOpacity = defaultShadowOpacity
}) {
    if (shadowOpacity < 1) {
        if (typeof color === "string") {
            color = new Color(color)
        }
        color.opacity = shadowOpacity
    }
    return ` drop-shadow(${xOffset}px ${yOffset}px ${shadowBlur}px ${color})`
}



//****************************** GRADIENTS


function setGradientStyle (screen, {linearGradient, radialGradient, fillColor, color}) {
    let gradParams
    let gradient

    if (linearGradient) {
        gradParams = linearGradient
        gradient   = createLinearGradient(screen, gradParams)
    } else {
        gradParams = radialGradient || {x: 0, y: 0, radius: 10}
        gradient   = createRadialGradient(screen, gradParams)
    }

    setGradientColors(gradient, gradParams.colors || defaultGradientColors({fillColor, color}))

    screen.cx.fillStyle        = gradient
    screen.currentCxStyle.fill = gradient
}


function createLinearGradient (screen, {origin, end}) {
    return screen.cx.createLinearGradient(
        origin.x,
        origin.y,
        end.x,
        end.y
    )
}


function createRadialGradient (screen, {innerCircle, outerCircle, center, radius}) {
    let innerCircleCenter
    let innerCircleRadius
    let outerCircleCenter
    let outerCircleRadius

    if (innerCircle) {
        innerCircleCenter = innerCircle
        innerCircleRadius = innerCircle.radius || 0
        outerCircleCenter = outerCircle
        const {radius: r = radius} = outerCircle
        outerCircleRadius = r
    } else {
        innerCircleCenter = center
        innerCircleRadius = 0
        outerCircleCenter = innerCircleCenter
        outerCircleRadius = radius
    }

    return screen.cx.createRadialGradient(
        innerCircleCenter.x,
        innerCircleCenter.y,
        innerCircleRadius,
        outerCircleCenter.x,
        outerCircleCenter.y,
        outerCircleRadius
    )
}


function setGradientColors (gradient, colors) {
    if (colors.length) {
        setGradientColorsFromArray(gradient, colors)
    } else {
        setGradientColorsFromObject(gradient, colors)
    }
}



function setGradientColorsFromArray (gradient, colors) {
    const colorsCount = colors.length
    for (let i = 0; i < colorsCount; i++) {
        gradient.addColorStop(i / (colorsCount - 1),  colors[i])
    }
}


function setGradientColorsFromObject (gradient, colors) {
    for (let percent in colors) {
        gradient.addColorStop(parseFloat(percent), colors[percent])
    }
}


function defaultGradientColors ({color, fillColor = color}) {
    return ["#000", fillColor || "#FFF"]
}




//****************************** TEXTURES


function hasFillStyleParams (params) {
    return Boolean(
        params.color || params.fillColor ||
        params.linearGradient || params.radialGradient ||
        params.texture
    )
}


function hasStrokeStyleParams (params) {
    return Boolean(params.strokeColor || params.strokeTexture)
}


const repeatToCanvasRepetition = {
    xy:   "repeat",
    x:    "repeat-x",
    y:    "repeat-y",
    none: "no-repeat"
}


function mapRepetition (repeat) {
    if (repeat === false) {
        return "no-repeat"
    }
    return repeatToCanvasRepetition[repeat] || "repeat"
}


let textureGhostCanvas
let textureGhostCx

function getTextureGhost () {
    if (!textureGhostCanvas) {
        textureGhostCanvas = newGhostCanvas()
        textureGhostCx     = textureGhostCanvas.getContext("2d")
    }
    return {canvas: textureGhostCanvas, cx: textureGhostCx}
}


function newGhostCanvas () {
    if (typeof document === "undefined") {
        return new OffscreenCanvas(10, 10)
    }
    return document.createElement("canvas")
}


function imageDimensions (image) { //eslint-disable-line complexity
    const width  = image.width  || image.naturalWidth  || image.videoWidth  || 0
    const height = image.height || image.naturalHeight || image.videoHeight || 0
    return {width, height}
}


const mirrorXValues  = {true: true, xy: true, x: true}
const mirrorYValues  = {true: true, xy: true, y: true}
const flipXValues    = {horizontal: true, h: true}
const flipYValues    = {vertical: true, v: true}

function mirrorAxes (mirror) {
    return {x: Boolean(mirrorXValues[mirror]), y: Boolean(mirrorYValues[mirror])}
}

function flipScales (flip) {
    return {
        x: flipXValues[flip] ? -1 : 1,
        y: flipYValues[flip] ? -1 : 1
    }
}


function mirrorCells (cropped, {mirrorX, mirrorY}, {baseSx, baseSy}) {
    const cells = [{atX: 0, atY: 0, sx: baseSx, sy: baseSy}]
    if (mirrorX) {
        cells.push({atX: cropped.width, atY: 0, sx: -baseSx, sy: baseSy})
    }
    if (mirrorY) {
        cells.push({atX: 0, atY: cropped.height, sx: baseSx, sy: -baseSy})
    }
    if (mirrorX && mirrorY) {
        cells.push({atX: cropped.width, atY: cropped.height, sx: -baseSx, sy: -baseSy})
    }
    return cells
}


function prepareTextureSource ({image, cropSource, flip, mirror}) { //eslint-disable-line complexity
    const {width: sourceWidth, height: sourceHeight} = imageDimensions(image)

    if (!cropSource && !flip && !mirror) {
        return {source: image, width: sourceWidth, height: sourceHeight}
    }

    const cropped = cropSource ?
        getCropParams({width: sourceWidth, height: sourceHeight}, cropSource) :
        {topLeftX: 0, topLeftY: 0, width: sourceWidth, height: sourceHeight}

    const mirrorAxis = mirrorAxes(mirror)
    const flipScale  = flipScales(flip)

    const tileW = cropped.width  * (mirrorAxis.x ? 2 : 1)
    const tileH = cropped.height * (mirrorAxis.y ? 2 : 1)

    const ghost = getTextureGhost()
    ghost.canvas.width  = tileW
    ghost.canvas.height = tileH
    ghost.cx.clearRect(0, 0, tileW, tileH)

    mirrorCells(cropped, {
        mirrorX: mirrorAxis.x,
        mirrorY: mirrorAxis.y
    }, {
        baseSx: flipScale.x,
        baseSy: flipScale.y
    }).forEach(cell => drawCell(ghost.cx, image, cropped, cell))

    return {source: ghost.canvas, width: tileW, height: tileH}
}


function drawCell (cx, image, cropped, {atX, atY, sx, sy}) {
    cx.save()
    cx.translate(
        atX + (sx < 0 ? cropped.width  : 0),
        atY + (sy < 0 ? cropped.height : 0)
    )
    cx.scale(sx, sy)
    cx.drawImage(
        image,
        cropped.topLeftX, cropped.topLeftY, cropped.width, cropped.height,
        0,                0,                cropped.width, cropped.height
    )
    cx.restore()
}


function patternScales (tex, sourceWidth, sourceHeight) { //eslint-disable-line complexity
    const hasW = "width"  in tex
    const hasH = "height" in tex

    if (hasW && hasH) {
        return {x: tex.width / sourceWidth, y: tex.height / sourceHeight}
    }
    if (hasW) {
        const s = tex.width / sourceWidth
        return {x: s, y: s}
    }
    if (hasH) {
        const s = tex.height / sourceHeight
        return {x: s, y: s}
    }
    const scale = (tex.scale === undefined) ? 1 : tex.scale
    if (typeof scale === "object") {
        return {x: scale.x, y: scale.y}
    }
    return {x: scale, y: scale}
}


function patternOrigin (tex, params) {
    if (tex.followShape && params._textureAnchor) {
        return params._textureAnchor
    }
    return tex.origin || {x: 0, y: 0}
}


//negate rotation for canvas y-down convention (positive in lib = ccw in user view)
function buildPatternTransform ({scaleX, scaleY, rotationDeg, originX, originY}) {
    const rad = -rotationDeg * Math.PI / 180 //eslint-disable-line no-magic-numbers
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    return {
        a:  cos * scaleX,
        b:  sin * scaleX,
        c: -sin * scaleY,
        d:  cos * scaleY,
        e: originX,
        f: originY
    }
}


function setTextureStyle (screen, params, target) { //eslint-disable-line complexity
    const tex = (target === "stroke") ? params.strokeTexture : params.texture
    if (!tex || !tex.image || typeof tex.image === "string") {
        return null
    }

    const {source, width: sourceWidth, height: sourceHeight} = prepareTextureSource(tex)
    if (!sourceWidth || !sourceHeight) {
        return null
    }

    const repetition = mapRepetition(tex.repeat)
    const pattern    = screen.cx.createPattern(source, repetition)
    if (!pattern) {
        return null
    }

    const {x: scaleX,  y: scaleY}  = patternScales(tex, sourceWidth, sourceHeight)
    const {x: originX, y: originY} = patternOrigin(tex, params)

    pattern.setTransform(buildPatternTransform({
        scaleX,
        scaleY,
        rotationDeg: tex.orientation || 0,
        originX,
        originY
    }))

    screen.canvasStyle.setSmoothing(screenImageUtils.resolveSmoothing(tex))

    if (target === "stroke") {
        screen.cx.strokeStyle        = pattern
        screen.currentCxStyle.stroke = pattern
    } else {
        screen.cx.fillStyle        = pattern
        screen.currentCxStyle.fill = pattern
    }

    return pattern
}





ScreenCanvasStyleHandler.hasFillStyleParams   = hasFillStyleParams
ScreenCanvasStyleHandler.hasStrokeStyleParams = hasStrokeStyleParams


export default ScreenCanvasStyleHandler

